/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDraggable } from 'react-use-draggable-scroll';
import OfficeSpace from './OfficeSpace/OfficeSpace';

import styles from './styles';
import { useTelegram } from '../../../hooks/useTelegram';
import { MeetingRoom } from './MeetingRoom/MeetingRoom';
import { OfficeSpaceBookingListResponse, OfficeSpaceListResponse } from '../../../api/types';
import { officeSpaceApi } from '../../../api/officeSpaceApi';
import { officeSpaceBookingApi } from '../../../api/officeSpaceBookingApi';
import { FullPageLoader } from '../FullPageLoader/FullPageLoader';
import { usersApi } from '../../../api/usersApi';
import { USER_TEAM } from '../../../constants';

type OfficeMapProps = {
    config?: MapConfig;
    selectedSpace?: string;
    startDate?: string | null;
    endDate?: string | null;
    applyDefaultConfig?: boolean;
    onOfficeSpaceClick?: (value: string) => void;
    onMeetingRoomClick?: () => void;
    userId?: string;
    // displays user info on the booked desk, otherwise - "Booked".
    showBookerDataForBookedDesks?: boolean;
};

export type MapConfig = {
    id: string;
    options: {
        disabled?: boolean;
        isBooked?: boolean;
        isMyBook?: boolean;
        render?: React.ReactNode | string;
        spaceTeams?: string[] | null;
    };
}[];

export function OfficeMap({
    config,
    selectedSpace,
    endDate,
    startDate,
    applyDefaultConfig,
    onOfficeSpaceClick,
    onMeetingRoomClick,
    userId = '',
    showBookerDataForBookedDesks,
}: OfficeMapProps) {
    const [mapConfig, setMapConfig] = useState<MapConfig | undefined>(config);

    const ref = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLDivElement>;

    const [getOfficeSpaces, { isLoading: areOfficeSpacesLoading }] =
        officeSpaceApi.useLazyListQuery();
    const [getOfficeSpaceBookings, { isLoading: areBookingsLoading }] =
        officeSpaceBookingApi.useLazyListQuery();
    const [getUsers, { isLoading: areUsersLoading }] = usersApi.useLazyListQuery();

    const { colorScheme } = useTelegram();
    const { events } = useDraggable(ref, { applyRubberBandEffect: true });

    const fetchOfficeSpaceBookings = useCallback(async () => {
        if (!startDate) return undefined;

        const response = await getOfficeSpaceBookings({
            filters: { startDate, endDate },
        }).unwrap();
        return response?.data ?? undefined;
    }, [startDate, endDate, getOfficeSpaceBookings]);

    const fetchOfficeSpaces = useCallback(async () => {
        const response = await getOfficeSpaces({}).unwrap();
        return response?.data ?? [];
    }, [getOfficeSpaces]);

    const fetchUser = useCallback(async () => {
        if (!userId) return null;

        const response = await getUsers({
            filters: { id: [userId] },
        }).unwrap();
        return response?.data?.[0]?.team ?? null;
    }, [userId, getUsers]);

    useEffect(() => {
        if (applyDefaultConfig) {
            const initializeMapConfig = async () => {
                const [officeSpaceBookings, officeSpaces, userTeams] = await Promise.all([
                    fetchOfficeSpaceBookings(),
                    fetchOfficeSpaces(),
                    fetchUser(),
                ]);

                const initialMapConfig = mapResponseToMapConfig({
                    bookings: officeSpaceBookings,
                    officeSpaces,
                    userTeams,
                    showBookerDataForBookedDesks,
                    userId,
                });

                const combinedConfig = combineConfigs({
                    initialConfig: initialMapConfig,
                    userConfig: config,
                });

                setMapConfig(combinedConfig);
            };

            initializeMapConfig();
        }
    }, [fetchOfficeSpaceBookings, fetchOfficeSpaces, fetchUser, applyDefaultConfig, config]);

    if (areBookingsLoading || areOfficeSpacesLoading || areUsersLoading) {
        return <FullPageLoader />;
    }

    return (
        <div {...events} ref={ref} css={styles.officeMapWrapperStyles({ colorScheme })}>
            <div css={styles.headerWrapperStyles}>
                <div css={styles.topManagementWrapperStyles}>
                    <div css={styles.topManagementRoomStyles({ colorScheme })}>
                        <p>TOP M.</p>
                    </div>
                    <MeetingRoom
                        id="MR1"
                        onClick={onMeetingRoomClick}
                        {...retrieveOptionsFromConfig({ config: mapConfig, id: 'MR1' })}
                    />
                </div>
                <div css={styles.enterStyles({ colorScheme })}>
                    <p className="enterText">Enter</p>
                </div>
                <div css={styles.wcStyles({ colorScheme })}>W/C</div>
                <div css={styles.kitchenLawAccWrapperStyles}>
                    <div css={styles.kitchenStyles({ colorScheme })}>Kitchen</div>
                    <div css={styles.lawAccStyles({ colorScheme })}>
                        <p>L&A</p>
                    </div>
                    {['MR2', 'MR3'].map((id) => (
                        <MeetingRoom
                            key={id}
                            id={id}
                            onClick={onMeetingRoomClick}
                            {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                        />
                    ))}
                </div>
            </div>
            <div css={styles.wrapperStyles}>
                <div css={styles.firstColumnWrapperStyles}>
                    <div css={styles.spaceLeftColumnStyles}>
                        {['37', '38', '39'].map((id) => (
                            <OfficeSpace
                                key={id}
                                id={id}
                                onClick={onOfficeSpaceClick}
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                selected={selectedSpace}
                            />
                        ))}
                    </div>
                    <div css={styles.spaceRightColumnStyles}>
                        {['36', '35', '34'].map((id) => (
                            <OfficeSpace
                                key={id}
                                id={id}
                                onClick={onOfficeSpaceClick}
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                selected={selectedSpace}
                            />
                        ))}
                    </div>
                </div>
                <div css={styles.secondColumnWrapperStyles}>
                    <div css={styles.spaceLeftColumnStyles}>
                        {['30', '31', '32', '33'].map((id) => (
                            <OfficeSpace
                                key={id}
                                id={id}
                                onClick={onOfficeSpaceClick}
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                selected={selectedSpace}
                            />
                        ))}
                    </div>
                    <div css={styles.spaceRightColumnStyles}>
                        {['29', '28', '27', '26'].map((id) => (
                            <OfficeSpace
                                key={id}
                                id={id}
                                onClick={onOfficeSpaceClick}
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                selected={selectedSpace}
                            />
                        ))}
                    </div>
                </div>
                <div css={styles.thirdColumnWrapperStyles}>
                    <div css={styles.spaceLeftColumnStyles}>
                        {['22', '23', '24', '25'].map((id) => (
                            <OfficeSpace
                                key={id}
                                id={id}
                                onClick={onOfficeSpaceClick}
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                selected={selectedSpace}
                            />
                        ))}
                    </div>
                    <div css={styles.spaceRightColumnStyles}>
                        {['21', '20', '19', '18'].map((id) => (
                            <OfficeSpace
                                key={id}
                                id={id}
                                onClick={onOfficeSpaceClick}
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                selected={selectedSpace}
                            />
                        ))}
                    </div>
                </div>
                <div css={styles.fourthColumnWrapperStyles}>
                    <div css={styles.spaceLeftColumnStyles}>
                        {['15', 'empty', '16', '17'].map((id) =>
                            id === 'empty' ? (
                                <div key="empty" css={styles.emptyRowStyles} />
                            ) : (
                                <OfficeSpace
                                    key={id}
                                    id={id}
                                    onClick={onOfficeSpaceClick}
                                    {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                    selected={selectedSpace}
                                />
                            ),
                        )}
                    </div>
                    <div css={styles.spaceRightColumnStyles}>
                        {['14', '13', '12', '11'].map((id) => (
                            <OfficeSpace
                                key={id}
                                id={id}
                                onClick={onOfficeSpaceClick}
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                selected={selectedSpace}
                            />
                        ))}
                    </div>
                </div>
                <div css={styles.fifthColumnWrapperStyles}>
                    <div css={styles.spaceLeftColumnStyles}>
                        {['8', '9', '10'].map((id) => (
                            <OfficeSpace
                                key={id}
                                id={id}
                                onClick={onOfficeSpaceClick}
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                selected={selectedSpace}
                            />
                        ))}
                    </div>
                    <div css={styles.spaceRightColumnStyles}>
                        {['5', '6', '7'].map((id) => (
                            <OfficeSpace
                                key={id}
                                id={id}
                                onClick={onOfficeSpaceClick}
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                selected={selectedSpace}
                            />
                        ))}
                    </div>
                </div>
                <div css={styles.sixthColumnWrapperStyles}>
                    <div css={styles.spaceLeftColumnStyles}>
                        {['3', '4'].map((id) => (
                            <OfficeSpace
                                key={id}
                                id={id}
                                onClick={onOfficeSpaceClick}
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                selected={selectedSpace}
                            />
                        ))}
                    </div>
                    <div css={styles.spaceRightColumnStyles}>
                        {['2', '1'].map((id) => (
                            <OfficeSpace
                                key={id}
                                id={id}
                                onClick={onOfficeSpaceClick}
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                selected={selectedSpace}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <div css={styles.windowWrapperStyles}>
                <div css={styles.windowStyles({ colorScheme })}>Window</div>
            </div>
        </div>
    );
}

function retrieveOptionsFromConfig({ config, id }: { config?: MapConfig; id?: string }) {
    const options = config?.find((record) => record.id === id);
    return options?.options;
}

function mapResponseToMapConfig({
    bookings,
    officeSpaces,
    userTeams,
    showBookerDataForBookedDesks,
    userId,
}: {
    userTeams?: USER_TEAM[] | null;
    bookings: OfficeSpaceBookingListResponse['data'] | undefined;
    officeSpaces: OfficeSpaceListResponse['data'];
    showBookerDataForBookedDesks?: boolean;
    userId?: string;
}): MapConfig {
    const config: MapConfig = officeSpaces.map((officeSpace) => {
        // we map through all bookings for particular date and desk
        // for example we want to know all bookings for desk 10 for 25 june
        const spaceBookings = bookings?.filter(
            (booking) => booking.space.name === officeSpace.name,
        );

        const currentUserBooking = spaceBookings?.find((booking) => booking.user.id === userId);

        const renderUserDetails = () => {
            if (spaceBookings) {
                const booking = spaceBookings[0];

                return (
                    <div>
                        <p>{booking.user.name}</p>
                        <p>{booking.user.surname}</p>
                    </div>
                );
            }
            return undefined;
        };

        // Now we have in particular desk one (showBookerDataForBookedDesks is true)
        // or more (showBookerDataForBookedDesks is false) bookings
        // If we do not have bookings - render "Desk ${value}"

        // 1 One day booking (showBookerDataForBookedDesks is true)
        // Add new boooking (userId) - show user name & surname
        // Edit existing booking (userId) - show user name & surname

        // 2 Multi day booking (showBookerDataForBookedDesks is false)
        // Add new boooking (userId):
        // - if in this period our user exist - show its name & surname
        // - other users are displayed as "Booked"
        // - if there are more than one booking in this period for one desk
        // - show "Booked"
        // Edit existing booking (userId) -
        // show its name & surname
        // - other users are displayed as "Booked"
        // - if there are more than one booking in this period for one desk

        let renderContent;

        if (spaceBookings && spaceBookings.length > 0) {
            if (showBookerDataForBookedDesks || currentUserBooking) {
                renderContent = renderUserDetails();
            } else {
                renderContent = <p>Booked</p>;
            }
        }

        return {
            id: officeSpace.name,
            options: {
                disabled: !userTeams
                    ? true
                    : userTeams?.every((team) => !officeSpace.bookingTeams?.includes(team)) ||
                      (spaceBookings && spaceBookings.length > 0),
                isBooked: spaceBookings && spaceBookings.length > 0,
                spaceTeams: officeSpace.bookingTeams?.map((team) => team),
                // We want to render {name & surnmae} or Booked
                // if we pass undefined, by default it renders desk value (25) -
                // it means desk is available for users (based on team assignment)
                render: renderContent,
            },
        };
    });

    return config;
}

function combineConfigs({
    initialConfig,
    userConfig,
}: {
    initialConfig: MapConfig;
    userConfig: MapConfig | undefined;
}): MapConfig {
    if (!userConfig) {
        return initialConfig;
    }

    const combinedConfig: MapConfig = initialConfig.map((initialConfigRecord) => {
        const userConfigRecord = userConfig.filter((item) => item.id === initialConfigRecord.id)[0];

        if (!userConfigRecord) {
            return initialConfigRecord;
        }

        const combinedRecord = {
            id: initialConfigRecord.id,
            options: { ...initialConfigRecord.options, ...userConfigRecord.options },
        };

        return combinedRecord;
    });

    return combinedConfig;
}
